import React, { useEffect, useState } from "react";
import axios from "axios";
import "./MyPolls.css";
import PollCard from "../components/PollCard";

// DTOs matching backend
interface PollOptionDto {
  optionId: string;
  optionText: string;
  emoji?: string;
  voteCount: number;
}

interface PollDto {
  pollId: string;
  title: string;
  category: string;
  primaryColor?: string;
  secondaryColor?: string;
  imageUrl?: string;
  createdByUserId?: string;
  createdAt: string;
  options: PollOptionDto[];
}

interface VoteRequestDto {
  optionId: string;
}

export default function MyPolls() {
  const [polls, setPolls] = useState<PollDto[]>([]);
  const [newPoll, setNewPoll] = useState<PollDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"createdAt" | "votes">("createdAt");

  const apiBase = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

  useEffect(() => {
    const fetchPolls = async () => {
      try {
        const token = localStorage.getItem("token");
        // Use the dedicated "mine" endpoint so only the logged-in user's polls are returned
        const res = await axios.get(`${apiBase}/api/polls/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPolls(res.data);
      } catch {
        alert("Failed to load polls");
      } finally {
        setLoading(false);
      }
    };
    fetchPolls();
  }, [apiBase]);

  const handleEdit = (poll: PollDto) => {
    // You can open a modal or inline form here; for now just log
    console.log("Edit poll", poll);
  };

  //const handleSave = async (poll: PollDto) => {
  //  try {
  //    const token = localStorage.getItem("token");
  //    await axios.put(`${apiBase}/api/polls/${poll.pollId}`, poll, {
  //      headers: { Authorization: `Bearer ${token}` },
  //    });
  //    setPolls(polls.map((p) => (p.pollId === poll.pollId ? poll : p)));
  //    alert("Poll updated!");
  //  } catch {
  //    alert("Failed to update poll");
  //  }
  //};

  const handleDelete = async (pollId: string) => {
    if (!window.confirm("Delete this poll?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${apiBase}/api/polls/${pollId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPolls(polls.filter((p) => p.pollId !== pollId));
      alert("Poll deleted!");
    } catch {
      alert("Failed to delete poll");
    }
  };

  const handleVote = async (pollId: string, optionId: string) => {
    try {
      const res = await axios.post(
        `${apiBase}/api/polls/${pollId}/vote`,
        { optionId } as VoteRequestDto,
        { headers: { "Content-Type": "application/json" } }
      );
      const updatedPoll = res.data;
      setPolls(polls.map((p) => (p.pollId === pollId ? updatedPoll : p)));
    } catch {
      alert("Vote failed");
    }
  };

  const handleCreate = async () => {
    if (!newPoll) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${apiBase}/api/polls`, newPoll, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPolls([...polls, res.data]);
      setNewPoll(null);
      alert("Poll created!");
    } catch {
      alert("Failed to create poll");
    }
  };

  const sortedPolls = [...polls].sort((a, b) => {
    if (sortBy === "createdAt") {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    } else {
      const votesA = a.options.reduce((sum, o) => sum + o.voteCount, 0);
      const votesB = b.options.reduce((sum, o) => sum + o.voteCount, 0);
      return votesB - votesA;
    }
  });

  if (loading) return <p>Loading polls...</p>;

  return (
    <div className="mypolls-container">
      <h2>My Polls</h2>

      {/* Create new poll */}
      <div className="new-poll card">
        <h3>Create New Poll</h3>
        <input
          type="text"
          placeholder="Title"
          value={newPoll?.title || ""}
          onChange={(e) =>
            setNewPoll({ ...(newPoll || ({} as PollDto)), title: e.target.value, options: [] })
          }
        />
        <input
          type="text"
          placeholder="Category"
          value={newPoll?.category || ""}
          onChange={(e) =>
            setNewPoll({ ...(newPoll || ({} as PollDto)), category: e.target.value, options: [] })
          }
        />
        {(newPoll?.options || []).map((opt, i) => (
          <input
            key={i}
            type="text"
            placeholder={`Option ${i + 1}`}
            value={opt.optionText}
            onChange={(e) => {
              const newOpts = [...(newPoll?.options || [])];
              newOpts[i] = { ...newOpts[i], optionText: e.target.value };
              setNewPoll({ ...(newPoll || ({} as PollDto)), options: newOpts });
            }}
          />
        ))}
        <button
          onClick={() =>
            setNewPoll({
              ...(newPoll || ({} as PollDto)),
              options: [...(newPoll?.options || []), { optionId: "", optionText: "", voteCount: 0 }],
            })
          }
        >
          Add Option
        </button>
        <button onClick={handleCreate}>Create Poll</button>
      </div>

      <div className="sort-controls">
        <label>Sort by: </label>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as "createdAt" | "votes")}>
          <option value="createdAt">Creation Date</option>
          <option value="votes">Total Votes</option>
        </select>
      </div>

      {sortedPolls.length === 0 && <p>No polls found.</p>}
      <div className="poll-grid">
        {sortedPolls.map((poll) => (
          <PollCard
            key={poll.pollId}
            poll={poll}
            onVote={handleVote}
            onEdit={handleEdit}
            onDelete={handleDelete}
            editable
          />
        ))}
      </div>
    </div>
  );
}
